// v1.2.0 - 2026-02-02 - Members list page with clickable detail links

import { useState, useEffect, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { useAuthStore } from '@/lib/stores/authStore'
import { api } from '@/lib/api/client'
import type { Member, MemberStatus, PaginatedResponse } from '@/types'
import type { MemberType } from '@/lib/ti-calculator/types'
import { PageContainer, PageHeader } from '@/components/layout/PageContainer'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Spinner } from '@/components/ui/spinner'
import { MemberFormModal } from '@/components/members/MemberFormModal'

export default function MembersPage() {
  const { user } = useAuthStore()
  const [members, setMembers] = useState<Member[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Filters
  const [search, setSearch] = useState('')
  const [typeFilter, setTypeFilter] = useState<MemberType | ''>('')
  const [statusFilter, setStatusFilter] = useState<MemberStatus | ''>('active')

  // Pagination
  const [page, setPage] = useState(1)
  const [total, setTotal] = useState(0)
  const [hasMore, setHasMore] = useState(false)
  const limit = 20

  // Modal state (placeholder for Task #19)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingMember, setEditingMember] = useState<Member | null>(null)

  const fetchMembers = useCallback(async () => {
    if (!user?.cer_id) return

    setIsLoading(true)
    setError(null)

    const response = await api.getMembers(user.cer_id, {
      search: search || undefined,
      type: typeFilter || undefined,
      status: statusFilter || undefined,
      page,
    })

    if (response.success && response.data) {
      const data = response.data as PaginatedResponse<Member>
      setMembers(data.items)
      setTotal(data.total)
      setHasMore(data.hasMore)
    } else {
      setError(response.error || 'Errore nel caricamento membri')
    }

    setIsLoading(false)
  }, [user?.cer_id, search, typeFilter, statusFilter, page])

  useEffect(() => {
    fetchMembers()
  }, [fetchMembers])

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(1)
      fetchMembers()
    }, 300)
    return () => clearTimeout(timer)
  }, [search, typeFilter, statusFilter])

  const handleAddMember = () => {
    setEditingMember(null)
    setIsModalOpen(true)
  }

  const handleEditMember = (member: Member) => {
    setEditingMember(member)
    setIsModalOpen(true)
  }

  const handleDeactivateMember = async (member: Member) => {
    if (!user?.cer_id) return
    if (!confirm(`Disattivare ${member.name}?`)) return

    const response = await api.deleteMember(user.cer_id, member.id)
    if (response.success) {
      fetchMembers()
    } else {
      alert('error' in response ? response.error : 'Errore nella disattivazione')
    }
  }

  const getTypeBadge = (type: MemberType) => {
    const variants: Record<MemberType, { label: string; variant: 'success' | 'info' | 'warning' }> = {
      producer: { label: 'Produttore', variant: 'success' },
      consumer: { label: 'Consumatore', variant: 'info' },
      prosumer: { label: 'Prosumer', variant: 'warning' },
    }
    return variants[type]
  }

  return (
    <PageContainer>
      <PageHeader
        title="Membri"
        description={`${total} membri ${statusFilter === 'active' ? 'attivi' : statusFilter === 'inactive' ? 'disattivati' : ''}`}
        actions={
          <Button onClick={handleAddMember}>
            + Aggiungi Membro
          </Button>
        }
      />

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
            <div className="flex-1">
              <Input
                placeholder="Cerca per nome, email o POD..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <div className="flex gap-2">
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value as MemberType | '')}
                className="h-10 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-primary)] px-3 text-sm"
              >
                <option value="">Tutti i tipi</option>
                <option value="producer">Produttore</option>
                <option value="consumer">Consumatore</option>
                <option value="prosumer">Prosumer</option>
              </select>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as MemberStatus | '')}
                className="h-10 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-primary)] px-3 text-sm"
              >
                <option value="active">Attivi</option>
                <option value="inactive">Disattivati</option>
                <option value="">Tutti</option>
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Members Table */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Spinner size="lg" />
            </div>
          ) : error ? (
            <div className="py-12 text-center text-[var(--color-error)]">{error}</div>
          ) : members.length === 0 ? (
            <div className="py-12 text-center text-[var(--color-text-secondary)]">
              Nessun membro trovato
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[var(--color-border)] bg-[var(--color-bg-secondary)]">
                    <th className="px-4 py-3 text-left text-sm font-medium text-[var(--color-text-secondary)]">
                      Nome
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-[var(--color-text-secondary)]">
                      Email
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-[var(--color-text-secondary)]">
                      Tipo
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-[var(--color-text-secondary)]">
                      POD
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-[var(--color-text-secondary)]">
                      CF/PIVA
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-[var(--color-text-secondary)]">
                      Status
                    </th>
                    <th className="px-4 py-3 text-right text-sm font-medium text-[var(--color-text-secondary)]">
                      Azioni
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {members.map((member) => {
                    const typeBadge = getTypeBadge(member.type)
                    return (
                      <tr
                        key={member.id}
                        className="border-b border-[var(--color-border)] hover:bg-[var(--color-bg-secondary)]/50"
                      >
                        <td className="px-4 py-3">
                          <Link
                            to={`/admin/members/${member.id}`}
                            className="font-medium text-[var(--color-text-primary)] hover:text-[var(--color-accent)] hover:underline"
                          >
                            {member.name}
                          </Link>
                          {member.is_company && (
                            <span className="ml-2 text-xs text-[var(--color-text-tertiary)]">Azienda</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-[var(--color-text-secondary)]">
                          {member.email}
                        </td>
                        <td className="px-4 py-3">
                          <Badge variant={typeBadge.variant}>{typeBadge.label}</Badge>
                        </td>
                        <td className="px-4 py-3 font-mono text-sm text-[var(--color-text-secondary)]">
                          {member.pod || '-'}
                        </td>
                        <td className="px-4 py-3 font-mono text-sm text-[var(--color-text-secondary)]">
                          {member.cf || '-'}
                        </td>
                        <td className="px-4 py-3">
                          <Badge variant={member.status === 'active' ? 'success' : 'default'}>
                            {member.status === 'active' ? 'Attivo' : 'Disattivato'}
                          </Badge>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEditMember(member)}
                            >
                              Modifica
                            </Button>
                            {member.status === 'active' && member.id !== user?.id && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeactivateMember(member)}
                                className="text-[var(--color-error)] hover:text-[var(--color-error)]"
                              >
                                Disattiva
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {!isLoading && members.length > 0 && (
            <div className="flex items-center justify-between border-t border-[var(--color-border)] px-4 py-3">
              <div className="text-sm text-[var(--color-text-secondary)]">
                Pagina {page} di {Math.ceil(total / limit)}
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(page - 1)}
                  disabled={page === 1}
                >
                  Precedente
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(page + 1)}
                  disabled={!hasMore}
                >
                  Successivo
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Member Form Modal */}
      <MemberFormModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false)
          setEditingMember(null)
        }}
        onSuccess={fetchMembers}
        editingMember={editingMember}
      />
    </PageContainer>
  )
}
